import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CarReservation, useAddTripItem } from "@/hooks/useTrips";
import { toast } from "sonner";

const carSchema = z.object({
  rental_company: z.string().min(1, "Rental company is required"),
  car_type: z.string().optional(),
  pickup_location: z.string().optional(),
  dropoff_location: z.string().optional(),
  pickup_time: z.string().min(1, "Pickup time is required"),
  dropoff_time: z.string().min(1, "Dropoff time is required"),
  confirmation_number: z.string().optional(),
});

type CarFormData = z.infer<typeof carSchema>;

interface AddCarDialogProps {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddCarDialog = ({
  tripId,
  open,
  onOpenChange,
}: AddCarDialogProps) => {
  const addItem = useAddTripItem();

  const form = useForm<CarFormData>({
    resolver: zodResolver(carSchema),
    defaultValues: {
      rental_company: "",
      car_type: "",
      pickup_location: "",
      dropoff_location: "",
      pickup_time: "",
      dropoff_time: "",
      confirmation_number: "",
    },
  });

  const onSubmit = (data: CarFormData) => {
    addItem.mutate(
      {
        tripId,
        itemType: "cars",
        item: data as CarReservation,
      },
      {
        onSuccess: () => {
          toast.success("Car rental added");
          form.reset();
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(`Failed to add: ${error.message}`);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Car Rental</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="rental_company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rental Company</FormLabel>
                    <FormControl>
                      <Input placeholder="Hertz" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="car_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Car Type</FormLabel>
                    <FormControl>
                      <Input placeholder="Compact SUV" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="pickup_location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pickup Location</FormLabel>
                  <FormControl>
                    <Input placeholder="Airport Terminal 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pickup_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pickup Time</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dropoff_location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dropoff Location</FormLabel>
                  <FormControl>
                    <Input placeholder="Airport Terminal 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dropoff_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dropoff Time</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmation_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirmation #</FormLabel>
                  <FormControl>
                    <Input placeholder="ABC123" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addItem.isPending}>
                {addItem.isPending ? "Adding..." : "Add Car Rental"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
